import React, { useState, useEffect } from "react";
import { useCookies } from "react-cookie";
import { useLocation, useNavigate } from "react-router-dom";

const styles = {
    page: {
        minHeight: "100vh",
        background: "linear-gradient(135deg, #0b0f14, #0a0a0c)",
        color: "#e7e7e9",
        padding: "48px 16px 72px",
        fontFamily: "'Pretendard', 'Noto Sans KR', system-ui, -apple-system, sans-serif",
        boxSizing: "border-box",
    },
    container: {
        maxWidth: "720px",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        gap: "16px",
    },
    card: {
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.07)",
        boxShadow: "0 20px 40px rgba(0,0,0,0.32)",
        borderRadius: "16px",
        padding: "22px 20px",
    },
    title: {
        margin: "0 0 6px",
        fontSize: "24px",
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "-0.02em",
    },
    helper: {
        color: "#cbd5e1",
        fontSize: "13px",
        marginBottom: "16px",
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: "14px",
    },
    label: {
        fontWeight: 700,
        marginBottom: "6px",
        display: "block",
        color: "#f8fafc",
    },
    input: {
        width: "100%",
        padding: "12px 14px",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.05)",
        color: "#f8fafc",
        fontSize: "15px",
        boxSizing: "border-box",
    },
    textarea: {
        width: "100%",
        padding: "12px 14px",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.05)",
        color: "#f8fafc",
        fontSize: "15px",
        boxSizing: "border-box",
        minHeight: "220px",
        resize: "vertical",
    },
    actions: {
        display: "flex",
        gap: "10px",
        justifyContent: "flex-end",
        marginTop: "6px",
    },
    primaryBtn: {
        padding: "12px 16px",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.12)",
        background: "linear-gradient(120deg, #fb923c, #f97316)",
        color: "#130903",
        fontWeight: 800,
        cursor: "pointer",
        boxShadow: "0 12px 28px rgba(249,115,22,0.26)",
    },
    ghostBtn: {
        padding: "12px 16px",
        borderRadius: "12px",
        border: "1px solid rgba(255,255,255,0.12)",
        background: "rgba(255,255,255,0.04)",
        color: "#f8fafc",
        fontWeight: 700,
        cursor: "pointer",
    },
};

const NoticeWrite = () => {
    const [cookies] = useCookies(["uuid"]);
    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [superUser, setSuperUser] = useState("");

    const location = useLocation();
    const navigate = useNavigate();

    // ✅ 권한 확인 함수
    const fetchUserInfo = (uuid) => {
        fetch(`/SetPage/admin_user?uuid=${uuid}`)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("서버 응답 실패");
                }
                return response.json();
            })
            .then((data) => {
                const user = data[0];
                if (user && (user.super_user === 1 || user.super_user === "1")) {
                    setSuperUser(user.super_user);
                } else {
                    alert("접근 권한이 없습니다.");
                    navigate("/", { replace: true });
                }
            })
            .catch((error) => {
                console.error("fetchUserInfo 오류:", error);
                alert("로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.");
                navigate("/LoginAts", { replace: true });
            });
    };

    // ✅ 쿠키나 경로 변경될 때 권한 체크
    useEffect(() => {
        if (!cookies.uuid) {
            alert("로그인 정보가 존재하지 않습니다. 로그인 페이지로 이동합니다.");
            navigate("/LoginAts", { replace: true });
        } else {
            fetchUserInfo(cookies.uuid);
        }
    }, [cookies.uuid, location.pathname]);

    // ✅ 공지 등록 처리
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!title.trim() || !content.trim()) {
            alert("제목과 내용을 모두 입력해주세요.");
            return;
        }

        try {
            const res = await fetch("/python/notice_add", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ title, content }),
            });

            if (res.ok) {
                alert("공지사항이 등록되었습니다.");
                navigate("/Notices");
            } else {
                alert("공지 등록 중 오류가 발생했습니다.");
            }
        } catch (err) {
            console.error(err);
            alert("서버와의 통신에 실패했습니다.");
        }
    };

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.card}>
                    <h2 style={styles.title}>새 공지사항 등록</h2>
                    <p style={styles.helper}>중요 공지를 등록해 사용자에게 전달하세요.</p>
                    <form onSubmit={handleSubmit} style={styles.form}>
                        <div>
                            <label style={styles.label}>제목</label>
                            <input
                                type="text"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="공지 제목을 입력하세요"
                                required
                                style={styles.input}
                            />
                        </div>
                        <div>
                            <label style={styles.label}>내용</label>
                            <textarea
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                placeholder="공지 내용을 입력하세요"
                                rows="8"
                                required
                                style={styles.textarea}
                            />
                        </div>
                        <div style={styles.actions}>
                            <button type="button" style={styles.ghostBtn} onClick={() => navigate(-1)}>
                                취소
                            </button>
                            <button type="submit" style={styles.primaryBtn}>
                                등록하기
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default NoticeWrite;
